import { TypedDocumentNode, gql } from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { CloseButton } from '@sorare/core/src/atoms/buttons/CloseButton';
import { Drawer } from '@sorare/core/src/atoms/layout/Drawer';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyL } from '@sorare/core/src/atoms/typography';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useGenerateDialogPath } from '@sorare/core/src/hooks/navigation/useGenerateDialogPath';
import { cart as cartMessages } from '@sorare/core/src/lib/glossary';
import {
  ClickRemoveFromCart_Origin,
  ClickSaveForLater_Origin,
} from '@sorare/core/src/protos/events/so5/shared/events';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import { regularMaterial } from '@sorare/core/src/style/utils';

import { CartItem } from 'components/cart/CartItem';
import { CartTotalSummary } from 'components/cart/CartTotalSummary';
import { useCart } from 'hooks/useCart';

import type {
  AddToCartPopinQuery,
  AddToCartPopinQueryVariables,
  AddToCartPopin_anyCard,
} from './__generated__/index.graphql';

interface Props {
  slugs: string[] | null;
  onClose: () => void;
}

const cardFragment = gql`
  fragment AddToCartPopin_anyCard on AnyCardInterface {
    slug
    rarityTyped
    ...CartItem_anyCard
  }
  ${CartItem.fragments.anyCard}
` as TypedDocumentNode<AddToCartPopin_anyCard>;

const ADD_TO_CART_POPIN_QUERY = gql`
  query AddToCartPopinQuery(
    $slugs: [String!]!
    $withCollectionPredictions: Boolean = true
  ) {
    anyCards(slugs: $slugs) {
      slug
      ...AddToCartPopin_anyCard
    }
  }
  ${cardFragment}
` as TypedDocumentNode<AddToCartPopinQuery, AddToCartPopinQueryVariables>;

const StyledDrawer = styled(Drawer)`
  width: 100%;
  @media ${tabletAndAbove} {
    bottom: auto;
    top: calc(8 * var(--unit));
    margin: var(--double-unit) var(--triple-unit);
    width: 480px;
    max-height: calc(70vh);
  }
`;

const Popin = styled(Vertical).attrs({ gap: 0 })`
  position: relative;
  isolation: isolate;
  ${regularMaterial}

  @media ${tabletAndAbove} {
    border-radius: var(--double-unit);
    overflow: hidden;
  }
`;

const CartItemsWrapper = styled(Vertical)`
  width: 100%;
  padding: var(--double-unit);
  overflow-y: auto;
  ${regularMaterial}
`;

const Title = styled(Horizontal)`
  position: sticky;
  top: 0;
  z-index: 1;
  ${regularMaterial}
  justify-content: space-between;
  padding: var(--double-unit);
  border-top-left-radius: var(--double-unit);
  border-top-right-radius: var(--double-unit);
  border-bottom: 1px solid var(--c-nd-150);
`;

const SummaryWrapper = styled(Vertical).attrs({ gap: 2 })``;

const Footer = styled(Vertical).attrs({ gap: 2 })`
  position: sticky;
  bottom: 0;
  z-index: 1;
  ${regularMaterial}
  border-top: 1px solid var(--c-nd-150);
  padding: var(--double-unit);
`;

const DURATION = 4000;

export const AddToCartPopin = ({ slugs, onClose }: Props) => {
  const { up: tabletOrAbove } = useScreenSize('tablet');
  const { cartCount, cart, cartTotal } = useCart();
  const generateDialogPath = useGenerateDialogPath();

  const [holdClosing, setHoldClosing] = useState(!tabletOrAbove);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

  const { data, loading } = useQuery(ADD_TO_CART_POPIN_QUERY, {
    variables: {
      slugs: slugs || [],
    },
    fetchPolicy: 'cache-first',
    skip: !slugs,
  });
  const anyCards = data?.anyCards;

  const cartItems = anyCards
    ?.map(c => {
      const cartItem = cart?.mainCartItems?.find(
        item => item.card.slug === c.slug
      );
      if (!cartItem) return undefined;
      return { cartItem, card: c };
    })
    .filter(Boolean);

  const onClean = useCallback(() => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
  }, [timeoutId]);

  useEffect(() => {
    if (holdClosing) {
      onClean();
      return () => {};
    }

    if (!timeoutId && !loading && slugs) {
      const tId = setTimeout(onClose, DURATION);
      setTimeoutId(tId);
      return () => {
        onClean();
      };
    }
    return () => {
      onClean();
    };
  }, [
    holdClosing,
    timeoutId,
    setHoldClosing,
    onClose,
    loading,
    onClean,
    slugs,
  ]);

  if (!slugs?.length || !anyCards || !cartItems) {
    return null;
  }

  const { sport } = anyCards?.[0] || {};

  const dialogRoute = generateDialogPath(DialogKey.checkout, sport);

  return (
    <StyledDrawer
      open={!!slugs}
      side={tabletOrAbove ? 'right' : 'bottom'}
      onBackdropClick={tabletOrAbove ? undefined : onClose}
    >
      <Popin
        onMouseEnter={() => setHoldClosing(true)}
        onMouseLeave={() => setHoldClosing(false)}
      >
        <Title>
          <BodyL bold>
            <FormattedMessage {...cartMessages.addedToCart} /> ({cartCount})
          </BodyL>
          <CloseButton onClose={onClose} />
        </Title>

        <CartItemsWrapper>
          {cartItems?.map(({ cartItem, card }) => (
            <CartItem
              key={cartItem?.id}
              cartItem={cartItem}
              card={card}
              onAction={cartItems?.length === 1 ? onClose : undefined}
              origin={{
                remove: ClickRemoveFromCart_Origin.ADD_TO_CART_POPIN,
                saveForLater: ClickSaveForLater_Origin.ADD_TO_CART_POPIN,
              }}
            />
          ))}
        </CartItemsWrapper>
        <Footer>
          <SummaryWrapper>
            <CartTotalSummary total={cartTotal} sport={sport} />
          </SummaryWrapper>
          <Horizontal>
            <Button
              size="medium"
              color="primary"
              onClick={onClose}
              fullWidth
              to={dialogRoute}
            >
              <FormattedMessage {...cartMessages.checkout} />
            </Button>
          </Horizontal>
        </Footer>
      </Popin>
    </StyledDrawer>
  );
};

AddToCartPopin.fragments = {
  anyCard: cardFragment,
};
